<template>
  <div class="run-card">
    <template v-if="!result">
      <h3>Importerar…</h3>
      <p class="sub">{{ bundle.sourceLabel }} · {{ bundle.files.map(f => f.name).join(', ') }}</p>
      <div class="total">
        <div class="bar"><div class="fill" :style="{ width: totalPct + '%' }"></div></div>
        <span class="total-num">{{ fmtInt(totalDone) }} / {{ fmtInt(totalAll) }}</span>
        <button class="btn-cancel" :disabled="cancelRequested" @click="cancel">{{ cancelRequested ? 'Avbryter…' : 'Avbryt' }}</button>
      </div>
      <p class="fine">Poster skickas i batchar. Avbryter du kan du köra samma fil igen senare – det som redan är sparat känns igen som dubbletter.</p>
      <ul class="plan">
        <li v-for="p in plan" :key="p.key" :class="stateOf(p.key)">
          <span class="p-icon">{{ p.icon }}</span>
          <span class="p-label">{{ p.label }}</span>
          <span class="p-prog">
            <template v-if="progress[p.key]">{{ fmtInt(progress[p.key].done) }} / {{ fmtInt(progress[p.key].total) }}</template>
            <template v-else>väntar</template>
          </span>
          <span class="p-cur">{{ progress[p.key]?.label || '' }}</span>
        </li>
      </ul>
    </template>

    <template v-else>
      <h3>{{ result.cancelled ? 'Import avbruten' : result.errors.length ? 'Import klar med fel' : 'Import klar!' }} <span v-if="bundle.mock" class="mock-tag">exempeldata</span></h3>
      <p class="sub">{{ bundle.sourceLabel }} · {{ took }}</p>

      <div class="stats">
        <div v-for="p in plan" :key="p.key" class="stat" :class="{ skipped: result.datasets[p.key]?.skipped }">
          <span class="s-icon">{{ p.icon }}</span>
          <span class="s-num">{{ fmtInt(result.datasets[p.key]?.done ?? 0) }}</span>
          <span class="s-lbl">{{ p.label.toLowerCase() }}</span>
          <span v-if="result.datasets[p.key]?.failed" class="s-err">{{ fmtInt(result.datasets[p.key].failed) }} fel</span>
          <span v-else-if="result.datasets[p.key]?.skipped" class="s-skip" :title="result.datasets[p.key].reason">{{ result.datasets[p.key].reason }}</span>
        </div>
      </div>

      <details v-if="result.errors.length" class="errors">
        <summary>Visa fel ({{ fmtInt(result.errors.length) }})</summary>
        <ul>
          <li v-for="(e, i) in result.errors.slice(0, 200)" :key="i"><strong>{{ e.label }}</strong> – {{ e.message }}</li>
          <li v-if="result.errors.length > 200">… och {{ fmtInt(result.errors.length - 200) }} till</li>
        </ul>
      </details>

      <div class="actions">
        <button class="btn-primary" @click="$emit('again')">Importera fler</button>
        <button class="btn-secondary" @click="$emit('goto', 'statistik')">Visa statistik</button>
        <button v-if="result.datasets.sessions?.done" class="btn-secondary" @click="$emit('goto', 'pass')">Visa pass</button>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue';
import { importState } from '../../../services/import/importSession.js';
import { fmtInt } from '../../../services/import/importModel.js';

export default {
  name: 'ImportRunView',
  emits: ['again', 'goto'],
  setup() {
    const bundle = computed(() => importState.bundle);
    const plan = computed(() => importState.plan);
    const progress = computed(() => importState.progress);
    const result = computed(() => importState.result);

    const totalAll = computed(() => plan.value.reduce((n, p) => n + p.items.length, 0));
    const totalDone = computed(() => plan.value.reduce((n, p) => n + (progress.value[p.key]?.done || 0), 0));
    const totalPct = computed(() => totalAll.value ? Math.round(totalDone.value / totalAll.value * 100) : 0);

    function stateOf(key) {
      const pr = progress.value[key];
      if (!pr) return 'pending';
      return pr.done >= pr.total ? 'done' : 'active';
    }
    const took = computed(() => {
      if (!result.value) return '';
      const s = Math.round((new Date(result.value.finishedAt) - new Date(result.value.startedAt)) / 1000);
      return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
    });

    const cancelRequested = computed(() => importState.cancelRequested);
    function cancel() { importState.cancelRequested = true; }

    return { bundle, plan, progress, result, totalAll, totalDone, totalPct, stateOf, took, cancelRequested, cancel, fmtInt };
  },
};
</script>

<style scoped>
.run-card { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg); padding: 1.25rem; box-shadow: var(--gym-shadow-sm); display: flex; flex-direction: column; gap: 0.9rem; }
h3 { margin: 0; font-size: 1.1rem; color: var(--gym-text); display: flex; gap: 0.6rem; align-items: center; }
.sub { margin: -0.5rem 0 0; font-size: 0.82rem; color: var(--gym-text-muted); }
.mock-tag { font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: var(--gym-radius-pill); background: var(--gym-warning-soft); color: var(--gym-warning); }

.total { display: flex; align-items: center; gap: 0.8rem; }
.bar { flex: 1; height: 10px; background: var(--gym-surface-3); border-radius: var(--gym-radius-pill); overflow: hidden; }
.fill { height: 100%; background: var(--gym-accent); border-radius: var(--gym-radius-pill); transition: width 0.25s ease; }
.total-num { font-weight: 800; color: var(--gym-text); font-variant-numeric: tabular-nums; }
.btn-cancel { background: var(--gym-surface-2); color: var(--gym-danger); border: 1px solid var(--gym-border); padding: 0.4rem 0.8rem; border-radius: var(--gym-radius-sm); font-size: 0.82rem; font-weight: 700; cursor: pointer; }
.btn-cancel:disabled { opacity: 0.6; cursor: wait; }
.fine { margin: -0.3rem 0 0; font-size: 0.76rem; color: var(--gym-text-faint); }

.plan { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.plan li { display: grid; grid-template-columns: 1.6rem 1fr auto; gap: 0.3rem 0.5rem; align-items: center; padding: 0.5rem 0.7rem; border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); border: 1px solid var(--gym-border); font-size: 0.88rem; color: var(--gym-text); }
.plan li.pending { opacity: 0.55; }
.plan li.active { border-color: var(--gym-accent-border); }
.plan li.done .p-prog::after { content: ' ✓'; color: var(--gym-success); }
.p-label { font-weight: 700; }
.p-prog { color: var(--gym-text-muted); font-variant-numeric: tabular-nums; }
.p-cur { grid-column: 2 / -1; font-size: 0.76rem; color: var(--gym-text-faint); min-height: 1em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.stats { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.6rem; }
.stat { display: flex; flex-direction: column; align-items: center; gap: 0.15rem; padding: 0.8rem 0.5rem; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius); text-align: center; }
.stat.skipped { opacity: 0.7; border-style: dashed; }
.s-icon { font-size: 1.2rem; }
.s-num { font-size: 1.9rem; font-weight: 800; color: var(--gym-accent); line-height: 1; }
.s-lbl { font-size: 0.8rem; color: var(--gym-text-muted); }
.s-err { font-size: 0.75rem; color: var(--gym-danger); font-weight: 700; }
.s-skip { font-size: 0.72rem; color: var(--gym-warning); font-weight: 600; }

.errors summary { cursor: pointer; color: var(--gym-danger); font-weight: 600; font-size: 0.88rem; }
.errors ul { margin: 0.5rem 0 0; padding-left: 1.2rem; font-size: 0.82rem; color: var(--gym-text-muted); line-height: 1.7; }

.actions { display: flex; gap: 0.6rem; flex-wrap: wrap; }
.btn-primary { background: var(--gym-accent); color: var(--gym-on-accent); border: none; padding: 0.6rem 1.3rem; border-radius: var(--gym-radius-sm); font-size: 0.92rem; font-weight: 700; cursor: pointer; }
.btn-primary:hover { background: var(--gym-accent-hover); }
.btn-secondary { background: var(--gym-surface-2); color: var(--gym-accent); border: 1.5px solid var(--gym-accent-border); padding: 0.55rem 1.15rem; border-radius: var(--gym-radius-sm); font-size: 0.92rem; font-weight: 700; cursor: pointer; }
.btn-secondary:hover { background: var(--gym-accent-soft); }
</style>
